import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Typography,
  Box,
  makeStyles,
} from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import axios from 'src/utils/baseAxios';
import axiosTechnician from 'src/utils/axios';
import ProductWorkCaseSearch from "src/components/Selects/ProductWorkCaseSearch";
import AppReasonSearch from "src/components/Selects/AppReasonSearch";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  field: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 14,
      backgroundColor: "#fff", // beyaz input zemini
      transition: "all 0.2s ease-in-out",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#4DA3FF", // hover border
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderWidth: 2,
        borderColor: "#1B84FF", // odaklanınca mavi vurgu
        boxShadow: "0 0 6px rgba(27,132,255,0.3)",
      },
    },
    "& .MuiAutocomplete-inputRoot": {
      borderRadius: 14,
      "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: 14,
      },
    },
    "& .MuiInputBase-input": {
      paddingTop: 14,
      paddingBottom: 14,
    },
  },
  sectionLabel: {
    fontSize: 15,
    fontWeight: 700,
    color: "#1b84ff",
    margin: theme.spacing(1.5, 0, 1),
    display: "inline-block",
    position: "relative",
    "&::after": {
      content: '""',
      display: "block",
      width: 40,
      height: 3,
      background: "linear-gradient(90deg, #00B2FF, #0079FF)",
      borderRadius: 2,
      marginTop: 4,
    },
  },
  datePicker: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 14,
      backgroundColor: "#fff",
    },
  },
  selectMenu: {
    "& .MuiMenuItem-root": {
      fontWeight: 500,
    },
  },
  formArea: {
  width: "100%",
  maxWidth: "100% !important",
}
}));

export default function RepairInfoBlock({ form, setForm, errors, handleChange, sessionTenantId }) {
  const classes = useStyles();
  const { t } = useTranslation('overview');
  const [warrantyTypes, setWarrantyTypes] = useState([]);
  const [preferredSolutions, setPreferredSolutions] = useState([]);

  const isTenant68 = sessionTenantId === 68;


  const GetWarranty = async () => {
      try {
        const res = await axios.get(`/Technician/GetSiriusWarrantyType`);
        setWarrantyTypes(res.data?.length ? res.data : []);
      } catch (err) {
        console.error("❌ GetWarranty error:", err);
        setWarrantyTypes([]);
      }
    };

  useEffect(() => {
    GetWarranty();
    axios.get("/tradepartner/preferredSolution").then((res) => setPreferredSolutions(res.data?.Data || []));
  }, []);

  // Tenant 68: applicationType → "Repair", warrantyType → "Warranty Repairs"
  useEffect(() => {
    if (!isTenant68) return;
    axiosTechnician.get('/GetSiriusIssueByProduct/Repair/0').then(res => {
      const item = (res.data || []).find(i => i.Dsc === 'Repair');
      if (item) setForm(s => ({ ...s, applicationType: item.Dsc, applicationTypeId: item.Id }));
    }).catch(() => {});
  }, [isTenant68]);

  useEffect(() => {
    if (!isTenant68 || !warrantyTypes.length) return;
    const match = warrantyTypes.find(w => w.FieldText === 'Warranty Repairs');
    if (match) setForm(s => ({ ...s, warrantyType: match.WarrantyId }));
  }, [isTenant68, warrantyTypes]);


  const onChange = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.value }));

  return (
    <Box mt={2} className={classes.formArea}>
      <Typography className={classes.sectionLabel}>
        🔧 {t("repairInfo", "Repair Info")}
      </Typography>
      <Grid container spacing={2}>
        {/* Product SKU */}
        <Grid item xs={12} sm={12}>
          <ProductWorkCaseSearch
            setProduct={(p) =>
              setForm((s) => ({
                ...s,
                productSku: p?.ModelCode || "",
                productSkuDesc: p?.Dsc || "",
                productSkuId: p?.Id || 0,
              }))
            }
            value={{
              Dsc: form.productSku || "",
              Id: form.productSkuId || 0,
              Dsc: form.productSkuDesc || "",
            }}
            error={form?.productSkuId > 0 ? false : !!errors.productSku}
            searchParam=""
            className={classes.field}
          />
        </Grid>

        {/* SerialNr */}
        <Grid item xs={12} sm={6}>
          <TextField
            className={classes.field}
            variant="outlined"
            fullWidth
            label={t("serialNr", "SerialNr")}
            value={form.serialNr}
            onChange={handleChange("serialNr")}
            error={!!errors.serialNr}
            helperText={errors.serialNr}
          />
        </Grid>

        {/* Application Type (AppReasonSearch) */}
        <Grid item xs={12} sm={6}>
          <AppReasonSearch
            setIssue={(issue) =>
              setForm((s) => ({
                ...s,
                applicationType: issue?.Dsc || "",
                applicationTypeId: issue?.Id || 0,
              }))
            }
            value={{
              Dsc: form.applicationType || "",
              Id: form.applicationTypeId || 0,
            }}
            searchParam=""
            error={!!errors.applicationType}
            helperText={errors.applicationType}
            disabled={isTenant68}
            className={classes.field}
          />
        </Grid>

        {/* Warranty Type */}
        <Grid item xs={12} sm={6}>
          <TextField
            className={classes.field}
            variant="outlined"
            select
            fullWidth
            label={t("warrantyType", "Warranty Type")}
            value={form.warrantyType}
            onChange={onChange("warrantyType")}
            disabled={isTenant68}
            error={!!errors.warrantyType}
            helperText={errors.warrantyType}
          >
            {warrantyTypes.map((w) => (
              <MenuItem key={w.WarrantyId} value={w.WarrantyId}>
                {w.FieldText}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Warranty Date */}
        <Grid item xs={12} sm={6}>
          <KeyboardDatePicker
            inputVariant="outlined"
            fullWidth
            className={classes.field}
            format="yyyy-MM-dd"
            label={t("warrantyDate", "Warranty Date")}
            value={form.warrantyDate}
            onChange={(d) =>
              setForm((s) => ({ ...s, warrantyDate: d }))
            }
            error={!!errors.warrantyDate}
            helperText={errors.warrantyDate}
          />
        </Grid>


        {/* Preferred Solution */}
        <Grid item xs={12} sm={3}>
          <TextField
            className={classes.field}
            variant="outlined"
            select
            fullWidth
            label={t("preferredSolution", "Preferred Solution")}
            value={form.preferredSolution}
            onChange={onChange("preferredSolution")}
            error={!!errors.preferredSolution}
            helperText={errors.preferredSolution}
          >
            {preferredSolutions.map((w) => (
              <MenuItem key={w.Id} value={w.Id}>
                {w.FieldText}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

         {/* Dealer Nr */}
         <Grid item xs={12} sm={3}>
          <TextField
            className={classes.field}
            variant="outlined"
            fullWidth
            label={t("dealerNr", "Dealer Nr")}
            value={form.dealerNr}
            onChange={handleChange("dealerNr")}
            error={!!errors.dealerNr}
            helperText={errors.dealerNr}
          />
         </Grid>
        
        

        {/* Comment */}
        <Grid item xs={12}>
          <TextField
            className={classes.field}
            variant="outlined"
            fullWidth
            multiline
            rows={3}
            label={t("comment", "Comment")}
            value={form.comment}
            onChange={handleChange("comment")}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
